import * as Yup from 'yup';
import { transactionTypePositionCheck } from '../utils/helper';
import {isEcommerceTransactionType} from "../resources/common/isEcommerceTransactionType";
import {
    isAnyProfitMarginTransactionType,
    isProfitMarginTransactionType
} from "../resources/common/isProfitMarginTransactionType";

const getRootValue = (context) => {
  if (context.from && Array.isArray(context.from) && context.from.length > 0) {
    return context.from[context.from.length - 1].value;
  }

  return context.parent || {};
};

const isExportTransaction = (transactionType) => transactionTypePositionCheck(transactionType, 7);
const isIOGTransaction = (transactionType) => transactionTypePositionCheck(transactionType, 13);
const requiresDeliveryAddressFields = (transactionType) =>
  isExportTransaction(transactionType) || isIOGTransaction(transactionType) || isEcommerceTransactionType(transactionType);

const getDeliveryCountryCode = (deliveryCountryCode) => {
  if (typeof deliveryCountryCode === 'object' && deliveryCountryCode !== null && 'code' in deliveryCountryCode) {
    return deliveryCountryCode.code;
  }

  return deliveryCountryCode;
};

export const createTransactionTypeFields = () => {

  return {
    transaction_type: Yup.string()
        .required("Transaction type is required")
        .test(
            "valid-transaction-type",
            "Invalid transaction type",
            function (value) {
              if (!value) {
                return this.createError({
                  path: "transaction_type",
                  message: "Transaction type is required",
                });
              }

              if (!/^[01]{20}$/.test(value)) {
                return this.createError({
                  path: "transaction_type",
                  message:
                      "Transaction type must contain exactly 20 binary characters",
                });
              }
              const has = (position) => value[position - 1] === "1";

              if (!has(1) && !has(2)) {
                return this.createError({
                  path: "transaction_type",
                  message:
                      "Either Full Tax Invoice or Simplified Invoice must be selected",
                });
              }

              if (has(1) && has(2)) {
                return this.createError({
                  path: "transaction_type",
                  message:
                      "Either Full Tax Invoice or Simplified Invoice must be selected. Cant be both.",
                });
              }

              const invalidRules = [
                [
                  3,
                  [4, 7],
                  "Self billed invoice cannot be combined with Third Party or Export invoice",
                ],

                [
                  4,
                  [3],
                  "Third Party invoice cannot be combined with Self billed invoice",
                ],

                [
                  5,
                  [6, 7, 10, 11, 13],
                  "Summary invoice cannot be combined with Continuous Supply, Export, Profit Margin, Profit Margin Self Invoice or Import of Goods",
                ],

                [
                  6,
                  [5, 8, 10, 11, 13],
                  "Continuous Supply invoice cannot be combined with Summary, Deemed Supply, Profit Margin, Profit Margin Self Invoice or Import of Goods",
                ],

                [
                  7,
                  [3, 5, 8, 9, 10, 11, 13],
                  "Export invoice cannot be combined with Self billed, Summary, Deemed Supply, Import of Service for RCM, Profit Margin, Profit Margin Self Invoice or Import of Goods",
                ],

                [
                  8,
                  [6, 7, 10, 11],
                  "Deemed Supply invoice cannot be combined with Continuous Supply, Export, Profit Margin or Profit Margin Self Invoice",
                ],

                [
                  9,
                  [7, 10, 11, 13],
                  "Import of Service for RCM cannot be combined with Export, Profit Margin, Profit Margin Self Invoice or Import of Goods",
                ],

                [
                  10,
                  [5, 6, 7, 8, 9, 13],
                  "Profit Margin invoice cannot be combined with Summary, Continuous Supply, Export, Deemed Supply, Import Service for RCM or Import of Goods",
                ],

                [
                  11,
                  [5, 6, 7, 8, 9, 10, 13],
                  "Profit Margin Self invoice cannot be combined with Summary, Continuous Supply, Export, Deemed Supply, Import of Service for RCM, Profit Margin or Import of Goods",
                ],

                [
                  12,
                  [11],
                  "E-Commerce Supply invoice cannot be combined with Profit Margin Self invoice",
                ],

                [
                  13,
                  [5, 6, 7, 9, 10, 11, 12],
                  "Import of Goods cannot be combined with Summary, Continuous Supply, Export, Import of Service for RCM, Profit Margin, Profit Margin Self Invoice or E-Commerce Supply",
                ],

                [
                  2,
                  [3, 4, 5, 7, 9, 10, 11, 13, 14],
                  "Simplified Tax Invoice cannot be combined with Self billed, Third Party, Summary, Import of Services for RCM, Export, Profit Margin, Profit Margin Self Invoice, Import of Goods or Special Zone Supplies",
                ],
              ];
              for (const [field, conflicts, message] of invalidRules) {
                if (has(field)) {
                  for (const conflict of conflicts) {
                    if (has(conflict)) {
                      return this.createError({
                        path: "transaction_type",
                        message,
                      });
                    }
                  }
                }
              }

              return true;
            },
        ),

    delivery_party_name: Yup.string().notRequired().nullable(),

    delivery_party_id: Yup.string().nullable()
        .test('required-when-import-of-goods', 'Delivery party id is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isIOGTransaction(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

    delivery_street_name: Yup.string().notRequired().nullable()
        .test('required-when-ecommerce', 'Delivery street name is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isEcommerceTransactionType(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

    delivery_address_line: Yup.string().notRequired().nullable()
        .test('required-when-ecommerce', 'Delivery address line is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isEcommerceTransactionType(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

    delivery_additional_street_name: Yup.string().notRequired().nullable()
        .test('required-when-ecommerce', 'Delivery additional street name is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isEcommerceTransactionType(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

    delivery_incoterms: Yup.string().nullable()
        .test('required-when-import-of-goods', 'Delivery incoterms is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isIOGTransaction(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

    delivery_city_name: Yup.string().notRequired().nullable()
        .test('required-when-ecommerce', 'Delivery city name is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isEcommerceTransactionType(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

    delivery_date: Yup.string().notRequired().nullable()
        .test('required-when-import-of-goods', 'Delivery date is required', function (value) {
      const root = getRootValue(this);
      const transactionType = root?.transaction_type;
      if (isIOGTransaction(transactionType)) {
        return value != null && value !== '';
      }
      return true;
    }),

    delivery_country_code: Yup.mixed()
      .nullable()
      .test('required-when-delivery-profile', "Country Code is required", function (value) {
        const root = getRootValue(this);
        const transactionType = root?.transaction_type;
        if (requiresDeliveryAddressFields(transactionType)) {
          return value != null;
        }

        return true;
      })
      .test('structure-when-delivery-profile', function (value) {
        const root = getRootValue(this);
        const transactionType = root?.transaction_type;
        if (requiresDeliveryAddressFields(transactionType)) {
          if (value == null) return true;
          if (typeof value === 'object') {
            const hasCode = !!value['code'];
            const hasLabel = !!value['label'];
            if (!hasCode || !hasLabel) {
              return this.createError({ message: "Country Code is required" });
            }
          }
        }

        return true;
      })
      .test('not-om-when-export', "Country Code cannot be OM for exports", function (value) {
        const root = getRootValue(this);
        const transactionType = root?.transaction_type;
        if (isExportTransaction(transactionType)) {
          if (!value) return true;
          let countryCode;
          if (typeof value === 'object' && 'code' in value) {
            countryCode = value.code;
          } else {
            countryCode = value;
          }
          if (countryCode === 'OM') {
            return this.createError({
              message: "Country Code cannot be OM for exports",
            });
          }
        }

        return true;
      }),

    delivery_country_subentity: Yup.mixed().notRequired().nullable(),

    delivery_postal_zone: Yup.string().notRequired().nullable()
        .test('required-when-ecommerce-invoice', 'Delivery postal code is required', function (value) {
          const root = getRootValue(this);
          const transactionType = root?.transaction_type;
          if (isEcommerceTransactionType(transactionType)) {
            return value != null && value !== '';
          }
          return true;
        }),

      billing_reference: Yup.array().notRequired().nullable()
          .test('required-for-profit-margin-invoice', 'Preceding Invoice Reference is required', function (value) {
              const root = getRootValue(this);
              const transactionType = root?.transaction_type;
              if(isProfitMarginTransactionType(transactionType)){
                  return value != null && value.length !== 0;
              }
              return true;
              }

          ),

  };
};
